import { MAX_PROFILE_ID_LENGTH, MAX_VISITED_PROFILE_IDS, gatewayErrorEnvelopeSchema, normalizeSpacePasswordText, openMeldRequestContextSchema, openMeldRequestedOrganizationSelectorSchema } from "@openmeld/schemas";
import { toErrorMessage as toErrorMessage$1 } from "@openmeld/utils";
//#region src/audit.ts
function contextAuditFields(context) {
	return {
		requestId: context.actorContext.requestId,
		openMeldProfileId: context.actorContext.openMeldProfileId,
		profileKind: context.actorContext.profileKind
	};
}
//#endregion
//#region src/headers.ts
const OPENMELD_HEADER_REQUEST_ID = "x-openmeld-request-id";
const OPENMELD_HEADER_CALLER_PRINCIPAL_ID = "x-openmeld-caller-principal-id";
const OPENMELD_HEADER_PROFILE_ID = "x-openmeld-profile-id";
const OPENMELD_HEADER_PROFILE_KIND = "x-openmeld-profile-kind";
const OPENMELD_HEADER_PROFILE_HANDLE = "x-openmeld-profile-handle";
const OPENMELD_HEADER_PROFILE_NAME = "x-openmeld-profile-name";
const OPENMELD_HEADER_OWNER_USER_ID = "x-openmeld-owner-user-id";
const OPENMELD_HEADER_OWNER_NAME = "x-openmeld-owner-name";
const OPENMELD_HEADER_AVATAR_URL = "x-openmeld-avatar-url";
const OPENMELD_HEADER_SPACE_PASSWORD = "x-openmeld-space-password";
const OPENMELD_HEADER_ACTIVE_ORGANIZATION_ID = "x-openmeld-active-organization-id";
const OPENMELD_HEADER_REQUESTED_ORGANIZATION_ID = "x-openmeld-requested-organization-id";
const OPENMELD_HEADER_REQUESTED_ORGANIZATION_SLUG = "x-openmeld-requested-organization-slug";
const OPENMELD_HEADER_AGENT_CONTROLLER_REF = "x-openmeld-agent-controller-ref";
const OPENMELD_HEADER_AGENT_CONTROLLER_CONVERSATION_ID = "x-openmeld-agent-controller-conversation-id";
const OPENMELD_HEADER_DISPATCH_CHAIN_ID = "x-openmeld-dispatch-chain-id";
const OPENMELD_HEADER_AUTO_RELAY_DEPTH = "x-openmeld-auto-relay-depth";
const OPENMELD_HEADER_VISITED_PROFILE_IDS = "x-openmeld-visited-profile-ids";
const OPENMELD_HEADER_VISITED_PROFILE_IDS_HASH = "x-openmeld-visited-profile-ids-hash";
const OPENMELD_HEADER_FORWARDED_BY = "x-openmeld-forwarded-by";
const OPENMELD_HEADER_INBOX_SOURCE_TARGET_VERSION = "x-openmeld-inbox-source-target-version";
const OPENMELD_HEADER_INBOX_ITEM_KIND_VERSION = "x-openmeld-inbox-item-kind-version";
const OPENMELD_HEADER_SPACE_ADDRESSING_VERSION = "x-openmeld-space-addressing-version";
const OPENMELD_SPACE_ADDRESSING_VERSION = "1";
const OPENMELD_HEADER_SPACE_FILES_ORIGIN_VERSION = "x-openmeld-space-files-origin-version";
const OPENMELD_SPACE_FILES_ORIGIN_VERSION = "1";
const OPENMELD_HEADER_SPACE_IMAGE_GEOMETRY_VERSION = "x-openmeld-space-image-geometry-version";
const OPENMELD_SPACE_IMAGE_GEOMETRY_VERSION = "1";
const OPENMELD_INBOX_ITEM_KIND_VERSION = "1";
const OPENMELD_HEADER_INBOX_SUMMARY_VERSION = "x-openmeld-inbox-summary-version";
const OPENMELD_INBOX_SUMMARY_VERSION = "3";
const OPENMELD_INBOX_SOURCE_TARGET_THREAD_UNAWARE_VERSION = "3";
const OPENMELD_INBOX_SOURCE_TARGET_VERSION = "9";
const OPENMELD_EXPECTED_FORWARDED_BY = "openmeld-gateway";
const MAX_HEADER_LENGTH = 128;
const MAX_ENCODED_HEADER_LENGTH = 1024;
const MAX_AVATAR_URL_HEADER_LENGTH = 2048;
const OPENMELD_HEADER_TEXT_ENCODING_PREFIX = "utf-8''";
const MAX_PROFILE_ID_LIST_HEADER_LENGTH = MAX_VISITED_PROFILE_IDS * MAX_PROFILE_ID_LENGTH + (MAX_VISITED_PROFILE_IDS - 1);
const MAX_ENCODED_PROFILE_ID_LIST_HEADER_LENGTH = MAX_VISITED_PROFILE_IDS * MAX_PROFILE_ID_LENGTH + (MAX_VISITED_PROFILE_IDS - 1) * 3 + 7;
const PRINTABLE_ASCII_PATTERN = /^[\x20-\x7e]*$/;
const OPENMELD_MANAGED_REQUEST_CONTEXT_HEADERS = [
	OPENMELD_HEADER_REQUEST_ID,
	OPENMELD_HEADER_CALLER_PRINCIPAL_ID,
	OPENMELD_HEADER_PROFILE_ID,
	OPENMELD_HEADER_PROFILE_KIND,
	OPENMELD_HEADER_PROFILE_HANDLE,
	OPENMELD_HEADER_PROFILE_NAME,
	OPENMELD_HEADER_OWNER_USER_ID,
	OPENMELD_HEADER_OWNER_NAME,
	OPENMELD_HEADER_AVATAR_URL,
	OPENMELD_HEADER_SPACE_PASSWORD,
	OPENMELD_HEADER_ACTIVE_ORGANIZATION_ID,
	OPENMELD_HEADER_REQUESTED_ORGANIZATION_ID,
	OPENMELD_HEADER_REQUESTED_ORGANIZATION_SLUG,
	OPENMELD_HEADER_AGENT_CONTROLLER_REF,
	OPENMELD_HEADER_AGENT_CONTROLLER_CONVERSATION_ID,
	OPENMELD_HEADER_DISPATCH_CHAIN_ID,
	OPENMELD_HEADER_AUTO_RELAY_DEPTH,
	OPENMELD_HEADER_VISITED_PROFILE_IDS,
	OPENMELD_HEADER_VISITED_PROFILE_IDS_HASH,
	OPENMELD_HEADER_FORWARDED_BY
];
function readOpenMeldRequestedOrganizationSelector(headers) {
	const organizationId = safeHeader(headers.get(OPENMELD_HEADER_REQUESTED_ORGANIZATION_ID));
	const organizationSlug = safeHeader(headers.get(OPENMELD_HEADER_REQUESTED_ORGANIZATION_SLUG));
	if (!(organizationId || organizationSlug)) return null;
	return openMeldRequestedOrganizationSelectorSchema.parse({
		...organizationId ? { organizationId } : {},
		...organizationSlug ? { organizationSlug } : {}
	});
}
/**
* Fetch header values are ByteStrings: undici and workerd throw a TypeError
* for any code point above 0xff, so user-authored text (profile names, owner
* names, Space passwords) cannot ride x-openmeld-* headers raw. Values that are
* not printable ASCII travel RFC 8187-style as `utf-8''<percent-encoded>`;
* printable-ASCII values pass through unchanged, keeping the wire format of
* machine identifiers identical to older writers.
*/
function encodeOpenMeldHeaderText(value) {
	if (PRINTABLE_ASCII_PATTERN.test(value) && !value.startsWith(OPENMELD_HEADER_TEXT_ENCODING_PREFIX)) return value;
	return `${OPENMELD_HEADER_TEXT_ENCODING_PREFIX}${encodeURIComponent(value)}`;
}
function decodeOpenMeldHeaderText(value) {
	if (!value.startsWith(OPENMELD_HEADER_TEXT_ENCODING_PREFIX)) return value;
	const encoded = value.slice(7);
	try {
		return decodeURIComponent(encoded);
	} catch {
		return value;
	}
}
function readOpenMeldRequestContext(headers, fallbackRequestId) {
	const requestId = safeHeader(headers.get("x-openmeld-request-id")) ?? fallbackRequestId;
	const openMeldProfileId = safeHeader(headers.get(OPENMELD_HEADER_PROFILE_ID));
	const profileKind = parseProfileKind(safeHeader(headers.get(OPENMELD_HEADER_PROFILE_KIND)));
	if (!(openMeldProfileId && profileKind)) return null;
	const parsed = openMeldRequestContextSchema.safeParse({
		actorContext: {
			requestId,
			...safeHeader(headers.get("x-openmeld-caller-principal-id")) ? { callerPrincipalId: safeHeader(headers.get(OPENMELD_HEADER_CALLER_PRINCIPAL_ID)) } : {},
			openMeldProfileId,
			profileKind,
			...safeHeader(headers.get("x-openmeld-profile-handle")) ? { profileHandle: safeHeader(headers.get(OPENMELD_HEADER_PROFILE_HANDLE)) } : {},
			...safeHeader(headers.get("x-openmeld-profile-name")) ? { profileName: safeHeader(headers.get(OPENMELD_HEADER_PROFILE_NAME)) } : {},
			...safeHeader(headers.get("x-openmeld-owner-user-id")) ? { ownerUserId: safeHeader(headers.get(OPENMELD_HEADER_OWNER_USER_ID)) } : {},
			...safeHeader(headers.get("x-openmeld-owner-name")) ? { ownerName: safeHeader(headers.get(OPENMELD_HEADER_OWNER_NAME)) } : {},
			...readAvatarUrlHeader(headers.get("x-openmeld-avatar-url")) ? { avatarUrl: readAvatarUrlHeader(headers.get(OPENMELD_HEADER_AVATAR_URL)) } : {},
			...safeHeader(headers.get("x-openmeld-agent-controller-ref")) ? { agentControllerRef: safeHeader(headers.get(OPENMELD_HEADER_AGENT_CONTROLLER_REF)) } : {},
			...safeHeader(headers.get("x-openmeld-dispatch-chain-id")) ? { dispatchChainId: safeHeader(headers.get(OPENMELD_HEADER_DISPATCH_CHAIN_ID)) } : {},
			...typeof parseOptionalNonNegativeIntHeader(headers.get("x-openmeld-auto-relay-depth")) === "number" ? { autoRelayDepth: parseOptionalNonNegativeIntHeader(headers.get(OPENMELD_HEADER_AUTO_RELAY_DEPTH)) } : {},
			...parseOptionalProfileIdListHeader(headers.get("x-openmeld-visited-profile-ids")).length > 0 ? { visitedProfileIds: parseOptionalProfileIdListHeader(headers.get(OPENMELD_HEADER_VISITED_PROFILE_IDS)) } : {},
			...safeHeader(headers.get("x-openmeld-visited-profile-ids-hash")) ? { visitedProfileIdsHash: safeHeader(headers.get(OPENMELD_HEADER_VISITED_PROFILE_IDS_HASH)) } : {}
		},
		...safeHeader(headers.get("x-openmeld-active-organization-id")) ? { activeOrganizationId: safeHeader(headers.get(OPENMELD_HEADER_ACTIVE_ORGANIZATION_ID)) } : {},
		...safeHeader(headers.get("x-openmeld-agent-controller-conversation-id")) ? { agentControllerConversationId: safeHeader(headers.get(OPENMELD_HEADER_AGENT_CONTROLLER_CONVERSATION_ID)) } : {}
	});
	return parsed.success ? parsed.data : null;
}
function buildOpenMeldRequestContextHeaders(input) {
	const { actorContext } = input.requestContext;
	const headers = {
		[OPENMELD_HEADER_REQUEST_ID]: actorContext.requestId,
		[OPENMELD_HEADER_PROFILE_ID]: actorContext.openMeldProfileId,
		[OPENMELD_HEADER_PROFILE_KIND]: actorContext.profileKind
	};
	const callerPrincipalId = normalize(actorContext.callerPrincipalId);
	if (callerPrincipalId) headers[OPENMELD_HEADER_CALLER_PRINCIPAL_ID] = callerPrincipalId;
	const profileHandle = normalize(actorContext.profileHandle);
	if (profileHandle) headers[OPENMELD_HEADER_PROFILE_HANDLE] = profileHandle;
	const profileName = normalize(actorContext.profileName);
	if (profileName) headers[OPENMELD_HEADER_PROFILE_NAME] = profileName;
	const ownerUserId = normalize(actorContext.ownerUserId);
	if (ownerUserId) headers[OPENMELD_HEADER_OWNER_USER_ID] = ownerUserId;
	const ownerName = normalize(actorContext.ownerName);
	if (ownerName) headers[OPENMELD_HEADER_OWNER_NAME] = ownerName;
	const avatarUrl = normalize(actorContext.avatarUrl);
	if (avatarUrl) headers[OPENMELD_HEADER_AVATAR_URL] = avatarUrl;
	const agentControllerRef = normalize(actorContext.agentControllerRef);
	if (agentControllerRef) headers[OPENMELD_HEADER_AGENT_CONTROLLER_REF] = agentControllerRef;
	const agentControllerConversationId = normalize(input.requestContext.agentControllerConversationId);
	if (agentControllerConversationId) headers[OPENMELD_HEADER_AGENT_CONTROLLER_CONVERSATION_ID] = agentControllerConversationId;
	const activeOrganizationId = normalize(input.requestContext.activeOrganizationId);
	if (activeOrganizationId) headers[OPENMELD_HEADER_ACTIVE_ORGANIZATION_ID] = activeOrganizationId;
	const dispatchChainId = normalize(actorContext.dispatchChainId);
	if (dispatchChainId) headers[OPENMELD_HEADER_DISPATCH_CHAIN_ID] = dispatchChainId;
	if (Number.isInteger(actorContext.autoRelayDepth) && (actorContext.autoRelayDepth ?? -1) >= 0) headers[OPENMELD_HEADER_AUTO_RELAY_DEPTH] = String(actorContext.autoRelayDepth);
	const visitedProfileIds = normalizeProfileIdList(actorContext.visitedProfileIds);
	if (visitedProfileIds.length > 0) headers[OPENMELD_HEADER_VISITED_PROFILE_IDS] = visitedProfileIds.join(",");
	const visitedProfileIdsHash = normalize(actorContext.visitedProfileIdsHash);
	if (visitedProfileIdsHash) headers[OPENMELD_HEADER_VISITED_PROFILE_IDS_HASH] = visitedProfileIdsHash;
	if (input.spacePassword !== void 0) {
		const spacePassword = normalizeSpacePasswordText(input.spacePassword);
		if (spacePassword.length > 0) headers[OPENMELD_HEADER_SPACE_PASSWORD] = spacePassword;
	}
	return encodeHeaderValues(headers);
}
function encodeHeaderValues(headers) {
	const encoded = {};
	for (const [key, value] of Object.entries(headers)) encoded[key] = encodeOpenMeldHeaderText(value);
	return encoded;
}
function applyOpenMeldRequestContextHeaders(target, input, forwardedBy = OPENMELD_EXPECTED_FORWARDED_BY) {
	for (const key of OPENMELD_MANAGED_REQUEST_CONTEXT_HEADERS) target.delete(key);
	const headers = buildOpenMeldRequestContextHeaders(input);
	for (const [key, value] of Object.entries(headers)) target.set(key, value);
	target.set(OPENMELD_HEADER_FORWARDED_BY, forwardedBy);
	return target;
}
function safeHeader(value, { maxEncodedLength = MAX_ENCODED_HEADER_LENGTH, maxLength = MAX_HEADER_LENGTH } = {}) {
	const normalized = normalize(value);
	if (!normalized) return null;
	const decoded = decodeOpenMeldHeaderText(normalized.slice(0, maxEncodedLength)).trim();
	if (!decoded) return null;
	return decoded.slice(0, maxLength);
}
function readAvatarUrlHeader(value) {
	return safeHeader(value, {
		maxEncodedLength: MAX_AVATAR_URL_HEADER_LENGTH,
		maxLength: MAX_AVATAR_URL_HEADER_LENGTH
	});
}
function normalize(value) {
	return String(value ?? "").trim();
}
function parseOptionalNonNegativeIntHeader(value) {
	const normalized = safeHeader(value);
	if (!normalized) return null;
	const parsed = Number.parseInt(normalized, 10);
	if (!Number.isInteger(parsed) || parsed < 0) return null;
	return parsed;
}
function parseOptionalProfileIdListHeader(value) {
	const normalized = safeHeader(value, {
		maxEncodedLength: MAX_ENCODED_PROFILE_ID_LIST_HEADER_LENGTH,
		maxLength: MAX_PROFILE_ID_LIST_HEADER_LENGTH
	});
	if (!normalized) return [];
	return normalizeProfileIdList(normalized.split(","));
}
function normalizeProfileIdList(value) {
	if (!Array.isArray(value)) return [];
	return value.map((item) => normalize(item)).filter((item) => item.length > 0);
}
function parseProfileKind(value) {
	if (value === "human" || value === "agent" || value === "system") return value;
	return null;
}
//#endregion
//#region src/errors.ts
function jsonErrorResponse(input) {
	const payload = gatewayErrorEnvelopeSchema.parse({
		ok: false,
		error: {
			code: input.code,
			message: input.message,
			requestId: input.requestId,
			...input.upstreamCode ? { upstreamCode: input.upstreamCode } : {}
		}
	});
	const response = Response.json(payload, { status: input.status });
	response.headers.set(OPENMELD_HEADER_REQUEST_ID, input.requestId);
	return response;
}
function wsErrorFrame(input) {
	const payload = gatewayErrorEnvelopeSchema.parse({
		ok: false,
		error: {
			code: input.code,
			message: input.message,
			requestId: input.requestId
		}
	});
	return JSON.stringify({
		type: "system.error",
		error: payload.error
	});
}
function withRequestIdHeader(response, requestId) {
	const headers = new Headers(response.headers);
	headers.set(OPENMELD_HEADER_REQUEST_ID, requestId);
	return new Response(response.body, {
		status: response.status,
		statusText: response.statusText,
		headers
	});
}
async function readUpstreamErrorMessage(response) {
	const text = (await response.text().catch(() => "")).trim();
	if (!text) return `upstream_http_${response.status}`;
	if (!(response.headers.get("content-type") ?? "").includes("application/json")) return text;
	let parsed;
	try {
		parsed = JSON.parse(text);
	} catch {
		return text;
	}
	if (typeof parsed !== "object" || parsed === null) return text;
	const body = parsed;
	const nestedMessage = typeof body.error === "object" && body.error !== null ? String(body.error.message ?? "").trim() : "";
	if (nestedMessage) return nestedMessage;
	const errorMessage = typeof body.error === "string" ? body.error.trim() : "";
	if (errorMessage) return errorMessage;
	const message = typeof body.message === "string" ? body.message.trim() : "";
	if (message) return message;
	return (typeof body.detail === "string" ? body.detail.trim() : "") || text;
}
const toErrorMessage = toErrorMessage$1;
//#endregion
//#region src/query-parameters.ts
const ORGANIZATION_DIRECTORY_INCLUDE_HUMAN_PROFILE_LAST_USED_AT_QUERY_PARAMETER = "includeHumanProfileLastUsedAt";
const ORGANIZATION_DIRECTORY_INCLUDE_MEMBER_LAST_ACTIVE_AT_QUERY_PARAMETER = "includeMemberLastActiveAt";
const ORGANIZATION_PROFILE_DIRECTORY_INCLUDE_MODEL_SELECTION_QUERY_PARAMETER = "includeModelSelection";
const ORGANIZATION_DIRECTORY_AGENT_PROFILE_WORK_RECOVERY_VERSION_QUERY_PARAMETER = "agentProfileWorkRecoveryVersion";
const ORGANIZATION_DIRECTORY_INCLUDE_TASK_ACTIVITY_QUERY_PARAMETER = "includeTaskActivity";
//#endregion
export { OPENMELD_EXPECTED_FORWARDED_BY, OPENMELD_HEADER_ACTIVE_ORGANIZATION_ID, OPENMELD_HEADER_AGENT_CONTROLLER_CONVERSATION_ID, OPENMELD_HEADER_AGENT_CONTROLLER_REF, OPENMELD_HEADER_AUTO_RELAY_DEPTH, OPENMELD_HEADER_AVATAR_URL, OPENMELD_HEADER_CALLER_PRINCIPAL_ID, OPENMELD_HEADER_DISPATCH_CHAIN_ID, OPENMELD_HEADER_FORWARDED_BY, OPENMELD_HEADER_INBOX_ITEM_KIND_VERSION, OPENMELD_HEADER_INBOX_SOURCE_TARGET_VERSION, OPENMELD_HEADER_INBOX_SUMMARY_VERSION, OPENMELD_HEADER_OWNER_NAME, OPENMELD_HEADER_OWNER_USER_ID, OPENMELD_HEADER_PROFILE_HANDLE, OPENMELD_HEADER_PROFILE_ID, OPENMELD_HEADER_PROFILE_KIND, OPENMELD_HEADER_PROFILE_NAME, OPENMELD_HEADER_REQUESTED_ORGANIZATION_ID, OPENMELD_HEADER_REQUESTED_ORGANIZATION_SLUG, OPENMELD_HEADER_REQUEST_ID, OPENMELD_HEADER_SPACE_ADDRESSING_VERSION, OPENMELD_HEADER_SPACE_FILES_ORIGIN_VERSION, OPENMELD_HEADER_SPACE_IMAGE_GEOMETRY_VERSION, OPENMELD_HEADER_SPACE_PASSWORD, OPENMELD_HEADER_VISITED_PROFILE_IDS, OPENMELD_HEADER_VISITED_PROFILE_IDS_HASH, OPENMELD_INBOX_ITEM_KIND_VERSION, OPENMELD_INBOX_SOURCE_TARGET_THREAD_UNAWARE_VERSION, OPENMELD_INBOX_SOURCE_TARGET_VERSION, OPENMELD_INBOX_SUMMARY_VERSION, OPENMELD_SPACE_ADDRESSING_VERSION, OPENMELD_SPACE_FILES_ORIGIN_VERSION, OPENMELD_SPACE_IMAGE_GEOMETRY_VERSION, ORGANIZATION_DIRECTORY_AGENT_PROFILE_WORK_RECOVERY_VERSION_QUERY_PARAMETER, ORGANIZATION_DIRECTORY_INCLUDE_HUMAN_PROFILE_LAST_USED_AT_QUERY_PARAMETER, ORGANIZATION_DIRECTORY_INCLUDE_MEMBER_LAST_ACTIVE_AT_QUERY_PARAMETER, ORGANIZATION_DIRECTORY_INCLUDE_TASK_ACTIVITY_QUERY_PARAMETER, ORGANIZATION_PROFILE_DIRECTORY_INCLUDE_MODEL_SELECTION_QUERY_PARAMETER, applyOpenMeldRequestContextHeaders, buildOpenMeldRequestContextHeaders, contextAuditFields, decodeOpenMeldHeaderText, encodeOpenMeldHeaderText, jsonErrorResponse, readOpenMeldRequestContext, readOpenMeldRequestedOrganizationSelector, readUpstreamErrorMessage, toErrorMessage, withRequestIdHeader, wsErrorFrame };

//# sourceMappingURL=index.js.map